import { useResourceParams } from '@refinedev/core';
import { createContext, ReactNode, useContext } from 'react';

import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { RESOURCES } from '@/Resources';

import { useUserContext } from './UserContext';

// ---- Permissions model ----
export type ResourcePermissionAction = 'list' | 'show' | 'create' | 'edit' | 'delete' | 'clone';
export type ResourcePermissions = Partial<Record<ResourcePermissionAction, boolean>>;

interface PermissionContextType {
  getPermissions: (resource: RESOURCES) => ResourcePermissions;
  hasPermission: (action: ResourcePermissionAction, res?: RESOURCES) => boolean;
  canAny: (resource: RESOURCES) => boolean;
  canAll: (resource: RESOURCES) => boolean;
}

const PermissionContext = createContext<PermissionContextType>({} as PermissionContextType);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { resource } = useResourceParams();
  const { userWorkArea, isBr } = useUserContext();

  return (
    <PermissionContext.Provider
      value={{
        getPermissions: (resource: RESOURCES) => resolvePermissions(userWorkArea, isBr, resource),
        hasPermission: (action, res = resource?.name as RESOURCES) =>
          hasPerm(resolvePermissions(userWorkArea, isBr, res), action),
        canAny: resource => hasAnyPerm(resolvePermissions(userWorkArea, isBr, resource)),
        canAll: resource => hasAllPerm(resolvePermissions(userWorkArea, isBr, resource)),
      }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionContext = () => useContext(PermissionContext);

// Helpers (non-exported) to safely resolve permissions
const emptyPermissions: ResourcePermissions = {
  list: false,
  show: false,
  create: false,
  edit: false,
  delete: false,
};

export function resolvePermissions(
  workArea: USER_WORK_AREA | undefined,
  isBr: boolean | undefined,
  resource: RESOURCES,
): ResourcePermissions {
  if (!workArea) return emptyPermissions;
  const effectivePermissionsByWorkArea = isBr ? permissionsByWorkAreaBr : permissionsByWorkArea;
  const byResource = effectivePermissionsByWorkArea[workArea];

  if (!byResource) return emptyPermissions;

  return byResource[resource] ?? emptyPermissions;
}

function hasPerm(perms: ResourcePermissions, action: ResourcePermissionAction): boolean {
  return Boolean(perms[action]);
}

function hasAnyPerm(perms: ResourcePermissions): boolean {
  return Object.values(perms).some(a => Boolean(a));
}

function hasAllPerm(perms: ResourcePermissions): boolean {
  return Object.values(perms).every(a => Boolean(a));
}

// ---- Presets de permisos reutilizables ----
/** Acceso completo: listar, ver, crear, editar y eliminar */
const FULL: ResourcePermissions = {
  list: true,
  show: true,
  create: true,
  edit: true,
  delete: true,
};
/** Solo lectura: listar y ver */
const READ_ONLY: ResourcePermissions = { list: true, show: true };
/** Gestión sin eliminación: listar, ver, crear y editar */
const MANAGE: ResourcePermissions = {
  list: true,
  show: true,
  create: true,
  edit: true,
  delete: false,
};

type WorkAreaPermissions = Partial<Record<RESOURCES, ResourcePermissions>>;

const permissionsByWorkArea: Partial<Record<USER_WORK_AREA, WorkAreaPermissions>> = {
  software: {
    [RESOURCES.TICKETS]: MANAGE,
    [RESOURCES.SHIPMENTS]: MANAGE,
    [RESOURCES.RECEPTIONS]: MANAGE,
    [RESOURCES.CASE_RESOLUTION]: READ_ONLY,
    [RESOURCES.SHIPPERS]: MANAGE,
    [RESOURCES.SHIPPING_LABELS]: FULL,
    [RESOURCES.SPARE_PARTS_COLLECTIONS]: FULL,
    [RESOURCES.SERVICE_ORDERS]: FULL,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  logistics: {
    [RESOURCES.TICKETS]: READ_ONLY,
    [RESOURCES.SHIPMENTS]: FULL,
    [RESOURCES.RECEPTIONS]: FULL,
    [RESOURCES.CASE_RESOLUTION]: READ_ONLY,
    [RESOURCES.SHIPPERS]: FULL,
    [RESOURCES.SHIPPING_LABELS]: FULL,
    [RESOURCES.SERVICE_ORDERS]: READ_ONLY,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  administration: {
    [RESOURCES.TICKETS]: READ_ONLY,
    [RESOURCES.CASE_RESOLUTION]: READ_ONLY,
    [RESOURCES.SHIPMENTS]: READ_ONLY,
    [RESOURCES.RECEPTIONS]: READ_ONLY,
    [RESOURCES.SPARE_PARTS_COLLECTIONS]: READ_ONLY,
    [RESOURCES.SERVICE_ORDERS]: READ_ONLY,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  customerservice: {
    [RESOURCES.TICKETS]: FULL,
    [RESOURCES.SHIPMENTS]: FULL,
    [RESOURCES.RECEPTIONS]: FULL,
    [RESOURCES.CASE_RESOLUTION]: READ_ONLY,
    [RESOURCES.SHIPPERS]: READ_ONLY,
    [RESOURCES.SHIPPING_LABELS]: FULL,
    [RESOURCES.SERVICE_ORDERS]: FULL,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  hardware: {
    [RESOURCES.TICKETS]: READ_ONLY,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  commercial: {
    [RESOURCES.TICKETS]: FULL,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
  // repairs: {
  // marketing: {
  //   [RESOURCES.TICKETS]: READ_ONLY,
  // },
  // repairs: {
  //   [RESOURCES.TICKETS]: READ_ONLY,
  // },
};
const permissionsByWorkAreaBr: Partial<Record<USER_WORK_AREA, WorkAreaPermissions>> = {
  ...permissionsByWorkArea,
  commercial: {
    [RESOURCES.TICKETS]: READ_ONLY,
    [RESOURCES.PRODUCT_CATALOG]: READ_ONLY,
  },
};
